import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import CommonDatePicker from '../DatePicker/CommonDatePicker';
import { createTodo, getTodoByRef, updateTodo } from '../../api/todoApi';
import '../../css/todo.css';

/**
 * To-Do 등록 레이어 (공통)
 *
 * Props:
 *   source       { ref_type, ref_table, ref_id, title } - 원본 등록구분 정보
 *   anchorRef    버튼 요소 ref (레이어 위치 기준)
 *   onClose      레이어 닫기 콜백
 *   onCreated    등록 완료 후 콜백 (선택)
 *
 * 사용 예시:
 *   <TodoRegisterLayer
 *     source={{ ref_type:'board', ref_table:'tdx_board', ref_id:1, title:'게시글 제목' }}
 *     anchorRef={btnRef}
 *     onClose={() => setOpen(false)}
 *     onCreated={() => alert('등록 완료')}
 *   />
 */

const DUE_OPTIONS = [
  { value: 'none', label: '기한 없음' },
  { value: 'today', label: '오늘' },
  { value: 'tomorrow', label: '내일' },
  { value: 'custom', label: '날짜 선택' },
];

const PRIORITY_OPTIONS = [
  { value: 'high', label: '높음' },
  { value: 'normal', label: '보통' },
  { value: 'low', label: '낮음' },
];

const parseDateOnly = (dateString) => {
  if (!dateString) return new Date();
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

function TodoRegisterLayer({ source, anchorRef, onClose, onCreated, direction = 'up', align = 'left' }) {
  const [title, setTitle] = useState(() => {
    let t = source?.title || '';
    if (source?.ref_type === 'pms_checklist') t += ' (체크리스트)';
    else if (source?.ref_type === 'pms_comment') t += ' (코멘트)';
    return t;
  });
  const [memo, setMemo] = useState(source?.memo || '');
  const [priority, setPriority] = useState('high');
  const [dueType, setDueType] = useState('none');
  const [dueDate, setDueDate] = useState(new Date()); // 날짜선택 기본: 오늘
  const [existingTodo, setExistingTodo] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const layerRef = useRef(null);
  const token = sessionStorage.getItem('token');

  useEffect(() => {
    let isMounted = true;

    const loadExistingTodo = async () => {
      if (!source?.ref_type || !source?.ref_id || !token) {
        setIsLoaded(true);
        return;
      }

      try {
        const todo = await getTodoByRef({ ref_type: source.ref_type, ref_id: source.ref_id }, token);
        if (!isMounted) return;

        if (todo) {
          setExistingTodo(todo);
          setTitle(t => todo.title || t);
          setMemo(todo.memo || '');
          setPriority(todo.priority || 'high');
          setDueType(todo.due_type || 'none');
          if (todo.due_type === 'custom' && todo.due_date) {
            setDueDate(parseDateOnly(todo.due_date));
          }
        }
      } catch (err) {
        if (!isMounted) return;
        setError('등록된 To-Do 조회 중 오류가 발생했습니다.');
      } finally {
        if (isMounted) setIsLoaded(true);
      }
    };

    loadExistingTodo();

    return () => {
      isMounted = false;
    };
  }, [source, token]);

  // 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        layerRef.current && !layerRef.current.contains(e.target) &&
        anchorRef?.current && !anchorRef.current.contains(e.target)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, anchorRef]);

  const handleSubmit = async () => {
    if (!title.trim()) { setError('제목을 입력해주세요.'); return; }
    if (dueType === 'custom' && !dueDate) { setError('기한일을 선택해주세요.'); return; }
    setError('');
    setLoading(true);
    try {
      const dueDateStr = dueType === 'custom'
        ? dueDate.toISOString().slice(0, 10)
        : null;

      const payload = {
        title: title.trim(),
        memo: memo || null,
        priority,
        due_type: dueType,
        due_date: dueDateStr,
        add_title: source.add_title,
        add_subtype: source.add_subtype,
        add_id1: source.add_id1,
        add_id2: source.add_id2,
      };

      if (existingTodo?.todo_id) {
        await updateTodo(existingTodo.todo_id, payload, token);
        alert('To-Do가 수정되었습니다.');
        onClose();
        return;
      }

      await createTodo({
        ref_type: source.ref_type,
        ref_table: source.ref_table,
        ref_id: source.ref_id,
        ...payload,
      }, token);

      alert('To-Do가 등록되었습니다.');
      if (onCreated) onCreated();
      onClose();
    } catch (err) {
      const serverMessage = err?.response?.data?.error;
      setError(serverMessage || (existingTodo ? '수정 중 오류가 발생했습니다.' : '등록 중 오류가 발생했습니다.'));
    } finally {
      setLoading(false);
    }
  };

  const isPms = source?.ref_type?.startsWith('pms_');

  const [portalStyle, setPortalStyle] = useState({ position: 'absolute', zIndex: 9999, opacity: 0 });

  useLayoutEffect(() => {
    if (anchorRef?.current && layerRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const layerRect = layerRef.current.getBoundingClientRect();

      let top, left;
      
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      let actualDirection = direction;
      if (actualDirection === 'down' && spaceBelow < layerRect.height + 10 && spaceAbove > spaceBelow) {
        actualDirection = 'up';
      } else if (actualDirection === 'up' && spaceAbove < layerRect.height + 10 && spaceBelow > spaceAbove) {
        actualDirection = 'down';
      }

      if (actualDirection === 'down') {
        top = rect.bottom + window.scrollY + 8;
      } else {
        top = rect.top + window.scrollY - layerRect.height - 8;
      }

      if (align === 'right') {
        left = rect.right + window.scrollX - layerRect.width;
      } else {
        left = rect.left + window.scrollX;
      }

      setPortalStyle({ top, left, position: 'absolute', zIndex: 9999, opacity: 1 });
    }
  }, [anchorRef, direction, align, isLoaded, existingTodo, error, memo, title]);

  const layerContent = (
    <div className={`todo-register-layer`} ref={layerRef} style={portalStyle}>
      {isLoaded && existingTodo && (
        <div className="todo-layer-registered-label">[등록된 To-Do - {existingTodo.status === 'complete' ? '완료' : '미완료'}]</div>
      )}

      {/* PMS 프로젝트 라벨 */}
      {isPms && source?.add_title && (
        <div className="todo-layer-row" style={{ minHeight: 'auto', marginBottom: '8px' }}>
          <div className="flex-grow-1" style={{ minWidth: 0, display: 'flex', alignItems: 'center' }}>
            <span 
              className="todo-layer-registered-label text-truncate d-block w-100" 
              style={{ color: '#0d6efd', padding: 0, margin: 0 }}
              title={`[프로젝트: ${source.add_title}]`}
            >
              [프로젝트: {source.add_title}]
            </span>
          </div>
        </div>
      )}

      {/* 제목 / 상세항목 */}
      <div className="todo-layer-row">
        <label className="todo-layer-label">
          {(() => {
            if (source?.ref_type === 'pms_feed') return '제목';
            if (source?.ref_type?.startsWith('pms_')) return '상세항목';
            return '제목';
          })()}
        </label>
        <input
          type="text"
          className="form-control form-control-sm"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
          placeholder="To-Do 제목"
        />
      </div>

      {/* 중요도 */}
      <div className="todo-layer-row todo-layer-row--chips">
        <label className="todo-layer-label">중요도</label>
        <div className="todo-layer-chip-group">
          {PRIORITY_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              className={`todo-layer-chip todo-layer-chip--priority-${opt.value} ${priority === opt.value ? 'active' : ''}`}
              onClick={() => setPriority(opt.value)}
              type="button"
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* 기한 옵션 */}
      <div className="todo-layer-row todo-layer-row--chips">
        <label className="todo-layer-label">기한</label>
        <div className="todo-layer-chip-group">
          {DUE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              className={`todo-layer-chip ${dueType === opt.value ? 'active' : ''}`}
              onClick={() => setDueType(opt.value)}
              type="button"
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* 날짜 표시 영역 (기한 있음일 때) */}
      {dueType !== 'none' && (
        <div className="todo-layer-row">
          <label className="todo-layer-label">날짜</label>
          {dueType === 'custom' ? (
            <CommonDatePicker
              value={dueDate}
              onChange={(date) => setDueDate(date)}
              placeholder="날짜 선택"
              inputClassName="form-control form-control-sm"
            />
          ) : (
            <input
              type="text"
              className="form-control form-control-sm"
              value={(() => {
                const now = new Date();
                const target = new Date();
                if (dueType === 'tomorrow') target.setDate(now.getDate() + 1);

                const y = target.getFullYear();
                const m = String(target.getMonth() + 1).padStart(2, '0');
                const d = String(target.getDate()).padStart(2, '0');
                return `${y}-${m}-${d}`;
              })()}
              readOnly
              disabled
              style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed', width: '120px' }}
            />
          )}
        </div>
      )}

      {/* 메모 */}
      <div className="todo-layer-row">
        <label className="todo-layer-label">메모</label>
        <textarea
          className="form-control form-control-sm"
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          maxLength={500}
          rows={4}
          placeholder="메모 (선택)"
        />
      </div>

      {/* 오류 메시지 */}
      {error && <div className="todo-layer-error">{error}</div>}

      {/* 버튼 */}
      <div className="todo-layer-actions">
        <button
          className="btn btn-primary btn-sm"
          onClick={handleSubmit}
          disabled={loading || !isLoaded}
        >
          {loading ? (existingTodo ? '수정 중...' : '등록 중...') : (existingTodo ? '수정' : '등록')}
        </button>
        <button className="btn btn-secondary btn-sm" onClick={onClose}>
          취소
        </button>
      </div>
    </div>
  );

  return createPortal(layerContent, document.body);
}

export default TodoRegisterLayer;
